import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import ObliqueAIIcon from '@/components/icons/ObliqueAIIcon';
import { createPortal } from 'react-dom';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — GlobalSearch (Command Palette)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * ⌘K / Ctrl+K Spotlight-style Command Palette.
 * 
 * Usage:
 *   import { GlobalSearch } from '@/core/oblique-ui'
 *   
 *   <GlobalSearch 
 *      isOpen={isOpen} 
 *      onClose={() => setIsOpen(false)} 
 *   />
 */

export function GlobalSearch({ isOpen, onClose }) {
    const [query, setQuery] = useState('');
    const navigate = useNavigate();

    // Reset query when opened
    useEffect(() => {
        if (isOpen) setQuery('');
    }, [isOpen]);

    // Keyboard navigation (Escape) is typically handled by parent, but let's handle it here too for safety
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[var(--z-modal-backdrop,6000)]"
                    />
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed top-[20%] left-1/2 -translate-x-1/2 w-[560px] max-w-[calc(100vw-48px)] bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.25)] z-[var(--z-modal,6001)] overflow-hidden"
                    >
                        {/* Search Input */}
                        <div className="p-4 px-5 border-b border-[var(--border)] flex items-center gap-3">
                            <ObliqueIcon name="search" size={20} color="var(--text-tertiary)" />
                            <input
                                autoFocus
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Search tools, reports, modules, or ask AI..."
                                className="flex-1 bg-transparent border-none outline-none shadow-none text-[15px] font-medium text-[var(--text-primary)] p-0 m-0 w-full placeholder:text-[var(--text-tertiary)]"
                            />
                            <span className="text-[11px] text-[var(--text-tertiary)] bg-[var(--bg-muted)] py-1 px-2 rounded-md font-medium">ESC</span>
                        </div>

                        {/* Quick Actions */}
                        <div className="p-2 max-h-[360px] overflow-y-auto custom-scrollbar">
                            {query.trim().length > 0 && (
                                <>
                                    <div className="px-3 pt-2 pb-1.5 text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.08em]">Siri-like AI Actions</div>
                                    <button
                                        onClick={() => { navigate(`/ai?q=${encodeURIComponent(query)}`); onClose(); setQuery(''); }}
                                        className="w-full flex items-center gap-3 p-2.5 bg-[#6c52e814] border border-[#6c52e833] cursor-pointer rounded-xl text-[13px] font-medium text-[#6c52e8] text-left transition-colors mb-2 hover:bg-[#6c52e81f]"
                                    >
                                        <ObliqueAIIcon size={18} />
                                        <span>Ask Oblique AI to <strong>"{query}"</strong></span>
                                    </button>
                                    <button
                                        onClick={() => { navigate(`/crm?q=${encodeURIComponent(query)}`); onClose(); setQuery(''); }}
                                        className="w-full flex items-center gap-3 p-2.5 bg-[var(--bg-subtle)] border-none cursor-pointer rounded-xl text-[13px] font-medium text-[var(--text-primary)] text-left transition-colors mb-3 hover:bg-[var(--bg-hover)]"
                                    >
                                        <ObliqueIcon name="search" size={18} color="var(--text-tertiary)" />
                                        <span>Search Deals & CRM for <strong>"{query}"</strong></span>
                                    </button>
                                </>
                            )}

                            {(() => {
                                const navItems = [
                                    { icon: 'support_agent', label: 'CRM — Operations', path: '/crm' },
                                    { icon: 'verified', label: 'Quality — Sales Audits', path: '/quality/sales-audits' },
                                    { icon: 'campaign', label: 'Quality — Campaigns', path: '/quality/campaigns' },
                                    { icon: 'warning', label: 'Quality — Escalations', path: '/quality/escalations' },
                                    { icon: 'bar_chart', label: 'Analytics Dashboard', path: '/analytics' },
                                    { icon: 'school', label: 'Academy — LMS', path: '/lms' },
                                    { icon: 'bolt', label: 'Orbit — Automations', path: '/automations' },
                                ].filter(i => i.label.toLowerCase().includes(query.toLowerCase()));

                                const toolItems = [
                                    { icon: 'settings', label: 'Settings', path: '/settings/preferences/profile' },
                                    { icon: 'group_add', label: 'Invite Team Members', path: '/settings/users/people' },
                                    { icon: 'receipt_long', label: 'Billing & Plans', path: '/settings/billing/subscription' },
                                ].filter(i => i.label.toLowerCase().includes(query.toLowerCase()));

                                return (
                                    <>
                                        {navItems.length > 0 && (
                                            <>
                                                <div className="px-3 pt-2 pb-1.5 text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.08em]">Navigate</div>
                                                {navItems.map(item => (
                                                    <button
                                                        key={item.path}
                                                        onClick={() => { navigate(item.path); onClose(); setQuery(''); }}
                                                        className="w-full flex items-center gap-3 p-2.5 bg-transparent border-none cursor-pointer rounded-xl text-[13px] font-medium text-[var(--text-primary)] text-left transition-colors hover:bg-[var(--bg-hover)]"
                                                    >
                                                        <ObliqueIcon name={item.icon} size={18} color="var(--text-tertiary)" />
                                                        {item.label}
                                                    </button>
                                                ))}
                                            </>
                                        )}

                                        {toolItems.length > 0 && (
                                            <>
                                                <div className={`px-3 pt-3 pb-1.5 text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.08em] ${navItems.length ? 'mt-2' : 'mt-0'}`}>Tools</div>
                                                {toolItems.map(item => (
                                                    <button
                                                        key={item.path}
                                                        onClick={() => { navigate(item.path); onClose(); setQuery(''); }}
                                                        className="w-full flex items-center gap-3 p-2.5 bg-transparent border-none cursor-pointer rounded-xl text-[13px] font-medium text-[var(--text-primary)] text-left transition-colors hover:bg-[var(--bg-hover)]"
                                                    >
                                                        <ObliqueIcon name={item.icon} size={18} color="var(--text-tertiary)" />
                                                        {item.label}
                                                    </button>
                                                ))}
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {/* Footer */}
                        <div className="p-2.5 px-4 border-t border-[var(--border)] flex justify-between text-[11px] text-[var(--text-tertiary)] bg-[var(--bg-surface)]">
                            <span>↑↓ Navigate</span>
                            <span>↵ Open</span>
                            <span className="flex items-center gap-1">
                                <ObliqueIcon name="auto_awesome" size={12} color="var(--text-tertiary)" /> AI powered — coming soon
                            </span>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
}
